import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { whatsappService } from "@/services/whatsapp";
import { toast } from "sonner";

interface WhatsAppQRModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WhatsAppQRModal({ open, onOpenChange }: WhatsAppQRModalProps) {
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchQR = async () => {
    setLoading(true);
    setError(false);
    try {
      const blob = await whatsappService.createSession();
      const url = URL.createObjectURL(blob);
      setQrUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch {
      setError(true);
      toast.error("No se pudo obtener el QR de WhatsApp");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchQR();
    } else {
      setQrUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[360px]">
        <DialogHeader>
          <DialogTitle>Conectar WhatsApp</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 py-4 min-h-[200px] justify-center">
          {loading && (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Generando QR...</p>
            </div>
          )}
          {error && !loading && (
            <div className="flex flex-col items-center gap-3">
              <p className="text-sm text-destructive">No se pudo obtener el QR</p>
              <Button variant="outline" size="sm" onClick={fetchQR}>
                Reintentar
              </Button>
            </div>
          )}
          {qrUrl && !loading && !error && (
            <>
              <img
                src={qrUrl}
                alt="WhatsApp QR"
                className="h-56 w-56 rounded-md border"
              />
              <p className="text-center text-sm text-muted-foreground">
                Escanea este QR con tu aplicación de WhatsApp para conectarte.
              </p>
              <Button variant="ghost" size="sm" onClick={fetchQR}>
                Generar nuevo QR
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
